import type { UserId } from "@core/domains/identity";
import { singleton } from "@core/foundation/class";
import { getBrandedString } from "@core/foundation/string";
import type { PK, SK } from "@core/ports/json-storage";
import { SK_VERSION_STRING } from "@core/ports/json-storage";
import type { ZodUnwrap, z } from "@core/zod";

import { BaseDeviceRepository } from "@react/ionic/storage";

import {
  Epoch,
  FIRST_EPOCH,
  FIRST_GRID_EPOCH,
} from "@apis/calorie-grid/models";

import { deviceStorage } from "../bootstrap/device-storage.ts";
import { TABLE_NAME } from "../bootstrap/storage.ts";

const identifierFields = { userId: true } as const;

const EpochIdentifierSchema = Epoch.schema.pick(identifierFields);

type EpochIdentifier = z.infer<typeof EpochIdentifierSchema>;

const EpochDataSchema = Epoch.schema.omit(identifierFields);

class EpochRepository extends BaseDeviceRepository<
  ZodUnwrap<typeof EpochIdentifierSchema>,
  ZodUnwrap<typeof EpochDataSchema>,
  Epoch,
  "epoch"
> {
  public static readonly getInstance = singleton(() => new EpochRepository());

  private constructor() {
    super({
      dataSchema: EpochDataSchema,
      deviceStorage,
      entityType: "epoch",
      identifierSchema: EpochIdentifierSchema,
      readSchema: Epoch.readSchema,
      resourceName: "Epoch",
      tableName: TABLE_NAME,
    });
  }

  public async ensureEpoch(userId: UserId): Promise<Epoch> {
    return this.getOrCreate({ userId }, () =>
      Epoch.create({
        epochId: FIRST_EPOCH,
        gridEpochId: FIRST_GRID_EPOCH,
        userId,
      }),
    );
  }

  protected getPK({
    userId,
  }: Pick<EpochIdentifier, "userId">): PK<typeof this.entityType> {
    return getBrandedString(`${this.entityType}#${userId}`);
  }

  protected getSK(): SK {
    return getBrandedString(SK_VERSION_STRING.v0);
  }
}

export const epochRepository = EpochRepository.getInstance();
